// weeklyProgress.js — Modal content for Weekly Production Progress KPI
// Place at: src/pages/components/modalContent/weeklyProgress.js

import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/weeklyProgress.css";
// Note: card sizing is controlled entirely by weeklyProgress.css.
// Height media queries (≤750px, ≤660px) compress cards so all 4
// fit inside a modal-large at 634 px viewport height.

// ─── Per-metric config ────────────────────────────────────────────────────────
const WEEKLY_METRICS = [
  {
    id: "units",
    label: "Weekly Units Completed",
    sublabel: "Daily Modules",
    actual: 8,
    goal: 12,
    unit: "units",
    modalTarget: "dailyModules",
    icon: (color) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        style={{ width: 22, height: 22 }}
      >
        <rect x="2" y="3" width="8" height="8" rx="1.5" />
        <rect x="14" y="3" width="8" height="8" rx="1.5" />
        <rect x="2" y="13" width="8" height="8" rx="1.5" />
        <rect x="14" y="13" width="8" height="8" rx="1.5" />
      </svg>
    ),
  },
  {
    id: "sqft",
    label: "SQFT Completed",
    sublabel: "Weekly Square Footage",
    actual: 4400,
    goal: 5000,
    unit: "sqft",
    modalTarget: "weeklySqft",
    icon: (color) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        style={{ width: 22, height: 22 }}
      >
        <path d="M3 9L12 3l9 6v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    id: "ontime",
    label: "On Time Completion",
    sublabel: "Schedule Variance",
    actual: 88.5,
    goal: 90.0,
    unit: "%",
    modalTarget: "completionVariance",
    icon: (color) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        style={{ width: 22, height: 22 }}
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16.5 14.5" />
      </svg>
    ),
  },
  {
    id: "quality",
    label: "Quality Pass Rate",
    sublabel: "First Pass — Utilities",
    actual: 92.0,
    goal: 92.0,
    unit: "%",
    modalTarget: "firstPassRate",
    icon: (color) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        style={{ width: 22, height: 22 }}
      >
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
      </svg>
    ),
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtValue = (v, unit) => {
  if (unit === "sqft")
    return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v);
  if (unit === "%") return `${v}%`;
  return String(v);
};

const pctOf = (actual, goal) =>
  Math.min(100, Math.round((actual / goal) * 100));

const statusOf = (pct) => {
  if (pct >= 100) return "ok";
  if (pct >= 85) return "amber";
  return "alert";
};

const STATUS_COLORS = {
  ok: {
    main: "#38a169",
    tint: "rgba(56,161,105,0.06)",
    border: "rgba(56,161,105,0.18)",
  },
  amber: {
    main: "#d69e2e",
    tint: "rgba(214,158,46,0.06)",
    border: "rgba(214,158,46,0.18)",
  },
  alert: {
    main: "#cc0000",
    tint: "rgba(204,0,0,0.05)",
    border: "rgba(204,0,0,0.15)",
  },
};

// ─── Segmented progress track ─────────────────────────────────────────────────

const SEGMENTS = 20;

const SegmentedBar = ({ pct, color }) => {
  const filled = Math.round((pct / 100) * SEGMENTS);
  return (
    <div className="wpm-segment-bar">
      {Array.from({ length: SEGMENTS }, (_, i) => {
        const isFilled = i < filled;
        const isEdge = isFilled && i === filled - 1;
        return (
          <div
            key={i}
            style={{
              flex: 1,
              height: 6,
              borderRadius: 2,
              background: isFilled ? color : "#e8edf3",
              opacity: isFilled ? 0.45 + (i / Math.max(filled, 1)) * 0.55 : 1,
              boxShadow: isEdge ? `0 0 7px ${color}99` : "none",
              transition: "background 0.3s",
            }}
          />
        );
      })}
    </div>
  );
};

// ─── Animated percentage counter ─────────────────────────────────────────────

const AnimatedPct = ({ value, color }) => {
  const elRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    let start = null;
    const duration = 650;
    const tick = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      if (elRef.current)
        elRef.current.textContent = `${Math.round(eased * value)}%`;
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);

  return (
    <span ref={elRef} className="wpm-animated-pct" style={{ color }}>
      0%
    </span>
  );
};

// ─── Single metric card ───────────────────────────────────────────────────────

const MetricCard = ({ metric, onNavigate, index }) => {
  const pct = pctOf(metric.actual, metric.goal);
  const status = statusOf(pct);
  const colors = STATUS_COLORS[status];
  const cardRef = useRef(null);

  const handleMouseEnter = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = "translateY(-2px)";
    cardRef.current.style.boxShadow = `0 8px 28px rgba(0,0,0,0.10), 0 0 0 1px ${colors.main}55`;
    cardRef.current.style.borderColor = colors.main;
  };
  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = "translateY(0)";
    cardRef.current.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
    cardRef.current.style.borderColor = colors.border;
  };

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onClick={() => onNavigate(metric.modalTarget)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onNavigate(metric.modalTarget);
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="wpm-card"
      style={{
        border: `1px solid ${colors.border}`,
        background: `linear-gradient(135deg, ${colors.tint} 0%, #fff 60%)`,
        animationDelay: `${index * 75}ms`,
      }}
    >
      {/* Left accent bar */}
      <div
        className="wpm-card-accent"
        style={{
          background: `linear-gradient(180deg, ${colors.main} 0%, ${colors.main}77 100%)`,
        }}
      />

      {/* Top row: icon + label + animated pct */}
      <div className="wpm-card-top-row">
        {/* Left: icon badge + text */}
        <div className="wpm-card-left">
          <div
            className="wpm-icon-badge"
            style={{
              background: `${colors.main}14`,
              border: `1px solid ${colors.main}28`,
            }}
          >
            {metric.icon(colors.main)}
          </div>
          <div>
            <div className="wpm-metric-label">{metric.label}</div>
            <div className="wpm-metric-sublabel">{metric.sublabel}</div>
          </div>
        </div>

        {/* Right: percentage + chevron */}
        <div className="wpm-pct-row">
          <AnimatedPct value={pct} color={colors.main} />
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="#c8d3de"
            strokeWidth="2"
            className="wpm-chevron"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      </div>

      {/* Segmented progress bar */}
      <SegmentedBar pct={pct} color={colors.main} />

      {/* Bottom row: actual / goal counts + status chip */}
      <div className="wpm-bottom-row">
        <div className="wpm-actual-goal">
          <span className="wpm-actual-value">
            {fmtValue(metric.actual, metric.unit)}
          </span>
          <span className="wpm-slash">/</span>
          <span className="wpm-goal-value">
            {fmtValue(metric.goal, metric.unit)} goal
          </span>
        </div>

        <div
          className="wpm-status-chip"
          style={{
            background: `${colors.main}14`,
            border: `1px solid ${colors.main}30`,
          }}
        >
          <div className="wpm-status-dot" style={{ background: colors.main }} />
          <span className="wpm-status-label" style={{ color: colors.main }}>
            {status === "ok"
              ? "On Target"
              : status === "amber"
                ? "Near Target"
                : "Off Target"}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const WeeklyProgress = () => {
  const navigate = useNavigate();

  const handleRowClick = (modalTarget) => {
    navigate("/production-milestones", { state: { openModal: modalTarget } });
  };

  const onTarget = WEEKLY_METRICS.filter(
    (m) => statusOf(pctOf(m.actual, m.goal)) === "ok",
  ).length;
  const overallPct = Math.round(
    WEEKLY_METRICS.reduce((sum, m) => sum + pctOf(m.actual, m.goal), 0) /
      WEEKLY_METRICS.length,
  );
  const overallColors = STATUS_COLORS[statusOf(overallPct)];

  return (
    <div className="wpm-container">
      {/* Summary header */}
      <div className="wpm-summary">
        <div>
          <div className="wpm-summary-label">Weekly Goal Progress</div>
          <div className="wpm-summary-subtext">
            {onTarget} of {WEEKLY_METRICS.length} metrics on target this week
          </div>
        </div>
        <div className="wpm-summary-right">
          <div
            className="wpm-overall-pct"
            style={{ color: overallColors.main }}
          >
            {overallPct}%
          </div>
          <div className="wpm-overall-avg-label">overall avg</div>
        </div>
      </div>

      {/* Cards */}
      {WEEKLY_METRICS.map((metric, index) => (
        <MetricCard
          key={metric.id}
          metric={metric}
          onNavigate={handleRowClick}
          index={index}
        />
      ))}

      <div className="wpm-footer-note">
        Click any card to open detailed analytics on the Production Milestones
        page
      </div>
    </div>
  );
};

export default WeeklyProgress;
